import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getSeasonBounds, toDateString } from "../utils/season";

const BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";
const getToken = () => localStorage.getItem("token");

async function adminRequest(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...options.headers,
    },
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || "發生錯誤");
    err.data = data;
    throw err;
  }
  return data;
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("zh-TW", {
    year: "numeric", month: "long", day: "numeric"
  });
}
function formatTime(t) { return t ? String(t).slice(0, 5) : ""; }

// ── Tab 元件 ──
function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, marginBottom: 32, borderBottom: "1px solid var(--stone)" }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)} style={{
          background: "none", border: "none", cursor: "pointer",
          padding: "10px 20px", fontSize: 15, fontWeight: 500,
          color: active === t.key ? "var(--ink)" : "var(--muted)",
          borderBottom: active === t.key ? "2px solid var(--ink)" : "2px solid transparent",
          marginBottom: -1,
        }}>{t.label}</button>
      ))}
    </div>
  );
}

// ================================================================
// 用戶管理
// ================================================================
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    adminRequest("/api/admin/users")
      .then(setUsers).finally(() => setLoading(false));
  }, []);

  const toggleActive = async (id) => {
    try {
      const result = await adminRequest(`/api/admin/users/${id}/toggle-active`, { method: "PATCH" });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, is_active: result.is_active } : u));
    } catch (err) { alert(err.message); }
  };

  const filtered = users.filter(u =>
    u.name.includes(search) || u.email.includes(search)
  );

  const roleLabel = { student: "學生", coach: "教練", admin: "管理員" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h3 style={{ fontSize: 20 }}>用戶列表（{users.length} 人）</h3>
        <input className="input-field" placeholder="搜尋姓名或 Email"
          style={{ width: 240 }} value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {loading ? <div className="spinner" style={{ margin: "40px auto" }} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(u => (
            <div key={u.id} className="card" style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%",
                  background: u.is_active ? "var(--ink)" : "var(--stone)",
                  color: "var(--white)", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 16, flexShrink: 0,
                }}>{u.name?.[0]}</div>
                <div>
                  <div style={{ fontWeight: 500 }}>{u.name}</div>
                  <div style={{ fontSize: 13, color: "var(--muted)" }}>{u.email}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className={`badge ${u.role === "admin" ? "badge-blue" : u.role === "coach" ? "badge-green" : "badge-gray"}`}>
                  {roleLabel[u.role]}
                </span>
                <span style={{ fontSize: 12, color: u.email_verified ? "var(--green)" : "var(--red)" }}>
                  {u.email_verified ? "已驗證" : "未驗證"}
                </span>
                <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px" }}
                  onClick={async () => {
                    const pwd = window.prompt(`重設 ${u.name} 的密碼（至少8字元）：`);
                    if (!pwd) return;
                    try {
                      await adminRequest(`/api/admin/users/${u.id}/password`, {
                        method: "PATCH", body: JSON.stringify({ password: pwd })
                      });
                      alert("密碼已重設");
                    } catch (err) { alert(err.message); }
                  }}>
                  改密碼
                </button>
                <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px",
                  color: u.is_active ? "var(--red)" : "var(--green)",
                  borderColor: u.is_active ? "#FECACA" : "#BBF7D0" }}
                  onClick={() => toggleActive(u.id)}>
                  {u.is_active ? "停用" : "啟用"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// 教練管理
// ================================================================
function CoachesPanel() {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(null);
  const [newCoach, setNewCoach] = useState({ email: "", name: "", password: "", price_per_hour: 1500, years_experience: 0, bio: "" });
  const [bulkForm, setBulkForm] = useState({ start_date: "", end_date: "", time_slots: [{ start_time: "09:00", end_time: "10:00" }], exclude_weekdays: [] });
  const [editRate, setEditRate] = useState(null); // { id, value }
  const [msg, setMsg] = useState("");

  useEffect(() => {
    adminRequest("/api/admin/coaches").then(setCoaches).finally(() => setLoading(false));
  }, []);

  const toggleActive = async (id) => {
    try {
    const result = await adminRequest(`/api/admin/coaches/${id}/toggle-active`, { method: "PATCH" });
    setCoaches(prev => prev.map(c => c.id === id ? { ...c, is_active: result.is_active } : c));
    } catch (err) { alert(err.message); }
  };

  const updateRate = async (id) => {
    if (!editRate?.value) return;
    try {
      await adminRequest(`/api/admin/coaches/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ price_per_hour: Number(editRate.value) }),
      });
      setEditRate(null);
      setCoaches(prev => prev.map(c => c.id === id ? { ...c, price_per_hour: Number(editRate.value) } : c));
    } catch (err) { alert(err.message); }
  };

  const handleAddCoach = async (e) => {
    e.preventDefault();
    try {
      await adminRequest("/api/admin/coaches", { method: "POST", body: JSON.stringify(newCoach) });
      setMsg("教練新增成功");
      setShowAdd(false);
      const updated = await adminRequest("/api/admin/coaches");
      setCoaches(updated);
    } catch (err) { alert(err.message); }
  };

  const handleBulkSlots = async (e) => {
    e.preventDefault();
    try {
      const result = await adminRequest(`/api/admin/coaches/${showBulk}/bulk-slots`, {
        method: "POST", body: JSON.stringify(bulkForm)
      });
      setMsg(result.message);
      setShowBulk(null);
    } catch (err) { alert(err.message); }
  };

  return (
    <div>
      {msg && <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "var(--green)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 14 }}>{msg}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <h3 style={{ fontSize: 20 }}>教練列表（{coaches.length} 位）</h3>
        <button className="btn btn-primary" style={{ padding: "8px 20px", fontSize: 14 }} onClick={() => setShowAdd(!showAdd)}>
          + 新增教練
        </button>
      </div>

      {/* 新增教練表單 */}
      {showAdd && (
        <div className="card" style={{ padding: 24, marginBottom: 20 }}>
          <h4 style={{ marginBottom: 16 }}>新增教練</h4>
          <form onSubmit={handleAddCoach} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label style={{ fontSize: 13, fontWeight: 500 }}>姓名</label>
              <input className="input-field" style={{ marginTop: 4 }} value={newCoach.name} onChange={e => setNewCoach(p => ({ ...p, name: e.target.value }))} required /></div>
            <div><label style={{ fontSize: 13, fontWeight: 500 }}>Email</label>
              <input className="input-field" style={{ marginTop: 4 }} type="email" value={newCoach.email} onChange={e => setNewCoach(p => ({ ...p, email: e.target.value }))} required /></div>
            <div><label style={{ fontSize: 13, fontWeight: 500 }}>初始密碼</label>
              <input className="input-field" style={{ marginTop: 4 }} type="password" value={newCoach.password} onChange={e => setNewCoach(p => ({ ...p, password: e.target.value }))} required /></div>
            <div><label style={{ fontSize: 13, fontWeight: 500 }}>每小時費率</label>
              <input className="input-field" style={{ marginTop: 4 }} type="number" value={newCoach.price_per_hour} onChange={e => setNewCoach(p => ({ ...p, price_per_hour: e.target.value }))} required /></div>
            <div><label style={{ fontSize: 13, fontWeight: 500 }}>教學年資</label>
              <input className="input-field" style={{ marginTop: 4 }} type="number" value={newCoach.years_experience} onChange={e => setNewCoach(p => ({ ...p, years_experience: e.target.value }))} /></div>
            <div style={{ gridColumn: "1 / -1" }}><label style={{ fontSize: 13, fontWeight: 500 }}>簡介</label>
              <textarea className="input-field" style={{ marginTop: 4, resize: "vertical" }} rows={3} value={newCoach.bio} onChange={e => setNewCoach(p => ({ ...p, bio: e.target.value }))} /></div>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
              <button type="submit" className="btn btn-primary" style={{ padding: "8px 24px" }}>建立</button>
              <button type="button" className="btn btn-outline" style={{ padding: "8px 24px" }} onClick={() => setShowAdd(false)}>取消</button>
            </div>
          </form>
        </div>
      )}

      {/* 批量新增時段表單 */}
      {showBulk && (
        <div className="card" style={{ padding: 24, marginBottom: 20 }}>
          <h4 style={{ marginBottom: 16 }}>批量新增時段</h4>
          <form onSubmit={handleBulkSlots} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={{ fontSize: 13, fontWeight: 500 }}>開始日期</label>
                <input className="input-field" style={{ marginTop: 4 }} type="date" value={bulkForm.start_date} onChange={e => setBulkForm(p => ({ ...p, start_date: e.target.value }))} required /></div>
              <div><label style={{ fontSize: 13, fontWeight: 500 }}>結束日期</label>
                <input className="input-field" style={{ marginTop: 4 }} type="date" value={bulkForm.end_date} onChange={e => setBulkForm(p => ({ ...p, end_date: e.target.value }))} required /></div>
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, display: "block" }}>排除星期</label>
              <div style={{ display: "flex", gap: 8 }}>
                {["日","一","二","三","四","五","六"].map((d, i) => (
                  <label key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={bulkForm.exclude_weekdays.includes(i)}
                      onChange={e => setBulkForm(p => ({
                        ...p,
                        exclude_weekdays: e.target.checked
                          ? [...p.exclude_weekdays, i]
                          : p.exclude_weekdays.filter(d => d !== i)
                      }))} />
                    週{d}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, display: "block" }}>時段</label>
              {bulkForm.time_slots.map((slot, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                  <input className="input-field" type="time" style={{ width: 120 }} value={slot.start_time}
                    onChange={e => setBulkForm(p => ({ ...p, time_slots: p.time_slots.map((s, j) => j === i ? { ...s, start_time: e.target.value } : s) }))} />
                  <span>—</span>
                  <input className="input-field" type="time" style={{ width: 120 }} value={slot.end_time}
                    onChange={e => setBulkForm(p => ({ ...p, time_slots: p.time_slots.map((s, j) => j === i ? { ...s, end_time: e.target.value } : s) }))} />
                  {bulkForm.time_slots.length > 1 && (
                    <button type="button" style={{ background: "none", color: "var(--red)", cursor: "pointer", fontSize: 18, border: "none" }}
                      onClick={() => setBulkForm(p => ({ ...p, time_slots: p.time_slots.filter((_, j) => j !== i) }))}>×</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px" }}
                onClick={() => setBulkForm(p => ({ ...p, time_slots: [...p.time_slots, { start_time: "09:00", end_time: "10:00" }] }))}>
                + 新增時段
              </button>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="submit" className="btn btn-primary" style={{ padding: "8px 24px" }}>批量新增</button>
              <button type="button" className="btn btn-outline" style={{ padding: "8px 24px" }} onClick={() => setShowBulk(null)}>取消</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="spinner" style={{ margin: "40px auto" }} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {coaches.map(c => (
            <div key={c.id} className="card" style={{ padding: "16px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", overflow: "hidden", background: "var(--stone)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {c.avatar_url ? <img src={c.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : c.name?.[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight: 500 }}>{c.name}</div>
                    <div style={{ fontSize: 13, color: "var(--muted)" }}>NT$ {Number(c.price_per_hour).toLocaleString()} / 小時 · {c.years_experience} 年經驗</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px" }}
                    onClick={() => setShowBulk(showBulk === c.id ? null : c.id)}>
                    批量時段
                  </button>
                  <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px" }}
                    onClick={() => setEditRate(editRate?.id === c.id ? null : { id: c.id, value: c.price_per_hour })}>
                    調整費率
                  </button>
                  <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px",
                    color: c.is_active ? "var(--red)" : "var(--green)",
                    borderColor: c.is_active ? "#FECACA" : "#BBF7D0" }}
                    onClick={() => toggleActive(c.id)}>
                    {c.is_active ? "停用" : "啟用"}
                  </button>
                </div>
                
                </div>
                {editRate?.id === c.id && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>新費率 NT$</span>
                    <input
                        className="input-field"
                        type="number"
                        style={{ width: 120, padding: "6px 10px", fontSize: 13 }}
                        value={editRate.value}
                        onChange={e => setEditRate(p => ({ ...p, value: e.target.value }))}
                    />
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>/ 小時</span>
                    <button className="btn btn-primary" style={{ fontSize: 13, padding: "6px 16px" }}
                        onClick={() => updateRate(c.id)}>
                        確認
                    </button>
                    <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 16px" }}
                        onClick={() => setEditRate(null)}>
                        取消
                    </button>
                </div>
                )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// 預約管理
// ================================================================
function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    adminRequest("/api/admin/bookings").then(setBookings).finally(() => setLoading(false));
  }, []);

  const forceCancel = async (id, refund, booking) => {
    const confirmMsg = refund
      ? `確定要強制取消預約 #${id} 並退款嗎？\n\n此操作將對學生「${booking.student_name}」全額退款 NT$ ${Number(booking.total_price).toLocaleString()}（不受一般退款規則限制，扣除 Stripe 手續費後退款）。\n\n此操作無法復原。`
      : `確定要強制取消預約 #${id} 嗎？\n\n此操作不會退款給學生「${booking.student_name}」。\n\n此操作無法復原。`;

    if (!window.confirm(confirmMsg)) return;

    const reason = window.prompt("請輸入取消原因（將記錄在系統中）：") || "管理員強制取消";
    if (reason === null) return;

    try {
      await adminRequest(`/api/admin/bookings/${id}/force-cancel`, {
        method: "PATCH", body: JSON.stringify({ refund, reason })
      });
      setMsg(`預約 #${id} 已取消${refund ? "並退款" : ""}`);
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status: "cancelled" } : b));
    } catch (err) { alert(err.message); }
  };

  const markComplete = async (id) => {
    try {
      await adminRequest(`/api/admin/bookings/${id}/complete`, { method: "PATCH" });
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status: "completed" } : b));
    } catch (err) { alert(err.message); }
  };

  const statusLabel = { pending: "待付款", confirmed: "已確認", cancelled: "已取消", completed: "已完成", refunded: "已退款" };
  const statusClass = { pending: "badge-gray", confirmed: "badge-green", cancelled: "badge-gray", completed: "badge-blue", refunded: "badge-gray" };

  const tabs = [
    { key: "all", label: "全部" },
    { key: "pending", label: "待付款" },
    { key: "confirmed", label: "已確認" },
    { key: "completed", label: "已完成" },
    { key: "cancelled", label: "已取消" },
  ];

  const filtered = activeTab === "all" ? bookings : bookings.filter(b => b.status === activeTab);

  return (
    <div>
      {msg && <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "var(--green)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 14 }}>{msg}</div>}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid var(--stone)" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "8px 16px", fontSize: 14,
            color: activeTab === t.key ? "var(--ink)" : "var(--muted)",
            borderBottom: activeTab === t.key ? "2px solid var(--ink)" : "2px solid transparent",
            marginBottom: -1,
          }}>{t.label} ({(t.key === "all" ? bookings : bookings.filter(b => b.status === t.key)).length})</button>
        ))}
      </div>
      {loading ? <div className="spinner" style={{ margin: "40px auto" }} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map(b => (
            <div key={b.id} className="card" style={{ padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                <div>
                  <span style={{ fontWeight: 500 }}>預約 #{b.id}</span>
                  <span style={{ margin: "0 8px", color: "var(--muted)" }}>·</span>
                  <span style={{ fontSize: 14 }}>
                    {b.student_name} → {b.coach_name || b.external_coach_name || "教練"} 教練
                    {b.assignment_type === "manual_external" && (
                      <span style={{ marginLeft: 6, fontSize: 12, color: "#5B21B6" }}>🧳 外派</span>
                    )}
                  </span>
                </div>
                <span className={`badge ${statusClass[b.status] || "badge-gray"}`}>{statusLabel[b.status] || b.status}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, padding: 12, background: "var(--off)", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
                <div><div style={{ color: "var(--muted)", marginBottom: 2 }}>日期</div><div style={{ fontWeight: 500 }}>{formatDate(b.booking_date)}</div></div>
                <div><div style={{ color: "var(--muted)", marginBottom: 2 }}>時間</div><div style={{ fontWeight: 500 }}>{formatTime(b.start_time)} – {formatTime(b.end_time)}</div></div>
                <div><div style={{ color: "var(--muted)", marginBottom: 2 }}>費用</div><div style={{ fontWeight: 500 }}>NT$ {Number(b.total_price).toLocaleString()}</div></div>
                <div><div style={{ color: "var(--muted)", marginBottom: 2 }}>付款</div>
                  <div style={{ fontWeight: 500, color: 
                    b.payment_status === "paid" ? "var(--green)" : 
                    b.payment_status === "refunded" ? "var(--blue)" : "var(--muted)" }}>
                    {b.payment_status === "paid" ? "已付款" : 
                     b.payment_status === "refunded" ? "已退款" : "未付款"}
                  </div>
                </div>
              </div>
              {b.stripe_payment_intent && (
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
                  Stripe: <a href={`https://dashboard.stripe.com/test/payments/${b.stripe_payment_intent}`} target="_blank" rel="noreferrer" style={{ color: "var(--blue)" }}>{b.stripe_payment_intent}</a>
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                {b.status === "confirmed" && (
                  <>
                    <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px" }} onClick={() => markComplete(b.id)}>標記完成</button>
                    <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px", color: "var(--red)", borderColor: "#FECACA" }} onClick={() => forceCancel(b.id, true, b)}>強制取消並退款</button>
                    <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px", color: "var(--red)", borderColor: "#FECACA" }} onClick={() => forceCancel(b.id, false, b)}>強制取消不退款</button>
                  </>
                )}
                {b.status === "pending" && (
                  <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px", color: "var(--red)", borderColor: "#FECACA" }} onClick={() => forceCancel(b.id, false, b)}>強制取消</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// 收入報表
// ================================================================
function RevenuePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminRequest("/api/admin/revenue").then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="spinner" style={{ margin: "40px auto" }} />;
  if (!data) return null;

  const { summary, monthly } = data;

  return (
    <div>
      <h3 style={{ fontSize: 20, marginBottom: 20 }}>收入報表</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 16 }}>
        {[
          { label: "總交易筆數", value: summary.total_transactions, unit: "筆" },
          { label: "總收入",     value: `NT$ ${Number(summary.total_revenue).toLocaleString()}`, unit: "" },
          { label: "總退款",     value: `NT$ ${Number(summary.total_refunded).toLocaleString()}`, unit: "" },
          { label: "退款筆數",   value: summary.total_refund_count, unit: "筆" },
        ].map((item, i) => (
          <div key={i} className="card" style={{ padding: 20 }}>
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>{item.label}</div>
            <div style={{ fontSize: 24, fontFamily: "'DM Serif Display', serif" }}>{item.value}{item.unit}</div>
          </div>
        ))}
      </div>

      {/* 收入拆分：基本收入 vs 指定教練獎勵金 vs 外派教練成本 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 16 }}>
        <div className="card" style={{ padding: 20, borderLeft: "4px solid var(--ink)" }}>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>基本收入（課程方案費用）</div>
          <div style={{ fontSize: 24, fontFamily: "'DM Serif Display', serif" }}>
            NT$ {Number(summary.total_base_revenue).toLocaleString()}
          </div>
        </div>
        <div className="card" style={{ padding: 20, borderLeft: "4px solid #F59E0B" }}>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>指定教練獎勵金（100% 歸教練）</div>
          <div style={{ fontSize: 24, fontFamily: "'DM Serif Display', serif" }}>
            NT$ {Number(summary.total_referral_bonus).toLocaleString()}
          </div>
        </div>
        <div className="card" style={{ padding: 20, borderLeft: "4px solid #DC2626" }}>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>外派教練成本</div>
          <div style={{ fontSize: 24, fontFamily: "'DM Serif Display', serif" }}>
            NT$ {Number(summary.total_external_coach_cost).toLocaleString()}
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 32, background: "var(--off)" }}>
        <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>淨收入（基本收入 − 外派教練成本）</div>
        <div style={{ fontSize: 28, fontFamily: "'DM Serif Display', serif", fontWeight: 600 }}>
          NT$ {Number(summary.total_net_revenue).toLocaleString()}
        </div>
      </div>

      <h4 style={{ fontSize: 17, marginBottom: 16 }}>每月收入</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {monthly.length === 0 ? (
          <div style={{ color: "var(--muted)", textAlign: "center", padding: 32 }}>還沒有收入記錄</div>
        ) : monthly.map((m, i) => (
          <div key={i} className="card" style={{ padding: "14px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontWeight: 500 }}>{m.month}</div>
              <div style={{ display: "flex", gap: 24 }}>
                <span style={{ color: "var(--muted)", fontSize: 14 }}>{m.transactions} 筆</span>
                <span style={{ fontWeight: 600 }}>NT$ {Number(m.revenue).toLocaleString()}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--muted)", paddingTop: 8, borderTop: "1px solid var(--stone)" }}>
              <span>基本收入：NT$ {Number(m.base_revenue).toLocaleString()}</span>
              <span>指定獎勵金：NT$ {Number(m.referral_bonus).toLocaleString()}</span>
              <span>外派成本：NT$ {Number(m.external_coach_cost).toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ================================================================
// 地點管理
// ================================================================
function LocationsPanel() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", address: "", description: "" });
  const [msg, setMsg] = useState("");

  useEffect(() => {
    adminRequest("/api/admin/locations").then(setLocations).finally(() => setLoading(false));
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const result = await adminRequest("/api/admin/locations", { method: "POST", body: JSON.stringify(form) });
      setLocations(prev => [result, ...prev]);
      setForm({ name: "", address: "", description: "" });
      setMsg("地點新增成功");
    } catch (err) { alert(err.message); }
  };

  const toggleActive = async (id) => {
    try {
      const result = await adminRequest(`/api/admin/locations/${id}/toggle-active`, { method: "PATCH" });
      setLocations(prev => prev.map(l => l.id === id ? result : l));
    } catch (err) { alert(err.message); }
  };

  return (
    <div>
      {msg && <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "var(--green)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 14 }}>{msg}</div>}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <h4 style={{ marginBottom: 16 }}>新增地點</h4>
        <form onSubmit={handleAdd} style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div><label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>地點名稱</label>
            <input className="input-field" style={{ width: 160 }} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required /></div>
          <div><label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>地址</label>
            <input className="input-field" style={{ width: 200 }} value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} /></div>
          <div><label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>說明</label>
            <input className="input-field" style={{ width: 200 }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
          <button type="submit" className="btn btn-primary" style={{ height: 46 }}>新增</button>
        </form>
      </div>
      {loading ? <div className="spinner" style={{ margin: "40px auto" }} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {locations.map(l => (
            <div key={l.id} className="card" style={{ padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 500 }}>{l.name}</div>
                {l.address && <div style={{ fontSize: 13, color: "var(--muted)" }}>{l.address}</div>}
              </div>
              <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px",
                color: l.is_active ? "var(--red)" : "var(--green)",
                borderColor: l.is_active ? "#FECACA" : "#BBF7D0" }}
                onClick={() => toggleActive(l.id)}>
                {l.is_active ? "停用" : "啟用"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// 課程方案管理
// ================================================================
function PackagesPanel() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editPrice, setEditPrice] = useState(null); // { id, value }
  const [msg, setMsg] = useState("");

  useEffect(() => {
    adminRequest("/api/admin/packages").then(setPackages).finally(() => setLoading(false));
  }, []);

  const toggleActive = async (pkg) => {
    try {
      const result = await adminRequest(`/api/admin/packages/${pkg.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !pkg.is_active }),
      });
      setPackages(prev => prev.map(p => p.id === pkg.id ? result : p));
    } catch (err) { alert(err.message); }
  };

  const updatePrice = async (id) => {
    if (!editPrice?.value) return;
    try {
      const result = await adminRequest(`/api/admin/packages/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ price: Number(editPrice.value) }),
      });
      setPackages(prev => prev.map(p => p.id === id ? result : p));
      setEditPrice(null);
      setMsg("價格更新成功");
    } catch (err) { alert(err.message); }
  };

  return (
    <div>
      {msg && <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "var(--green)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 14 }} onClick={() => setMsg("")}>{msg}</div>}
      <h3 style={{ fontSize: 20, marginBottom: 20 }}>課程方案管理</h3>
      {loading ? <div className="spinner" style={{ margin: "40px auto" }} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {packages.map(pkg => (
            <div key={pkg.id} className="card" style={{ padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{pkg.name}</div>
                  <div style={{ fontSize: 13, color: "var(--muted)" }}>
                    {formatTime(pkg.start_time)} – {formatTime(pkg.end_time)} · 計費 {pkg.duration_hours} 小時
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 20 }}>
                    NT$ {Number(pkg.price).toLocaleString()}
                  </div>
                  <span className={`badge ${pkg.is_active ? "badge-green" : "badge-gray"}`}>
                    {pkg.is_active ? "啟用中" : "已停用"}
                  </span>
                  <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px" }}
                    onClick={() => setEditPrice(editPrice?.id === pkg.id ? null : { id: pkg.id, value: pkg.price })}>
                    調整價格
                  </button>
                  <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 14px",
                    color: pkg.is_active ? "var(--red)" : "var(--green)",
                    borderColor: pkg.is_active ? "#FECACA" : "#BBF7D0" }}
                    onClick={() => toggleActive(pkg)}>
                    {pkg.is_active ? "停用" : "啟用"}
                  </button>
                </div>
              </div>
              {editPrice?.id === pkg.id && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--stone)", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>新價格 NT$</span>
                  <input className="input-field" type="number" style={{ width: 140, padding: "6px 10px", fontSize: 13 }}
                    value={editPrice.value}
                    onChange={e => setEditPrice(p => ({ ...p, value: e.target.value }))} />
                  <button className="btn btn-primary" style={{ fontSize: 13, padding: "6px 16px" }}
                    onClick={() => updatePrice(pkg.id)}>確認</button>
                  <button className="btn btn-outline" style={{ fontSize: 13, padding: "6px 16px" }}
                    onClick={() => setEditPrice(null)}>取消</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// 待指派通知
// ================================================================
const ASSIGN_REASONS = ["原教練請假", "原教練已滿班", "其他"];

function PendingPanel() {
  const [pending, setPending] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  // { bookingId, mode: "internal", coachId }
  // { bookingId, mode: "external", form: {...} }
  const [msg, setMsg] = useState("");

  const defaultExternalForm = {
    name: "", phone: "", fee: "", reason: "", notes: "",
    certification_note: "", save_to_favorites: false,
  };

  useEffect(() => {
    Promise.all([
      adminRequest("/api/admin/notifications/pending"),
      adminRequest("/api/admin/coaches"),
      adminRequest("/api/admin/external-coach-favorites"),
    ]).then(([p, c, f]) => { setPending(p); setCoaches(c); setFavorites(f); }).finally(() => setLoading(false));
  }, []);

  const handleSelectChange = (bookingId, value) => {
    if (!value) { setAssigning(null); return; }
    if (value === "external") {
      setAssigning({ bookingId, mode: "external", form: { ...defaultExternalForm } });
    } else {
      setAssigning({ bookingId, mode: "internal", coachId: value });
    }
  };

  const updateExternalForm = (patch) => {
    setAssigning(prev => prev ? { ...prev, form: { ...prev.form, ...patch } } : prev);
  };

  const applyFavorite = (favoriteId) => {
    const fav = favorites.find(f => String(f.id) === String(favoriteId));
    if (!fav) return;
    updateExternalForm({ name: fav.name, phone: fav.phone || "", certification_note: fav.certification_note || "" });
  };

  const handleAssignInternal = async (bookingId, force = false) => {
    if (!assigning?.coachId) { alert("請先選擇教練"); return; }
    try {
      await adminRequest(`/api/admin/bookings/${bookingId}/assign-coach`, {
        method: "PATCH",
        body: JSON.stringify({ coach_id: assigning.coachId, force_despite_conflict: force }),
      });
      setPending(prev => prev.filter(p => p.id !== bookingId));
      setAssigning(null);
      setMsg(`預約 #${bookingId} 已成功指派教練`);
    } catch (err) {
      if (err.data?.conflict) {
        const fmt = (t) => String(t).slice(0, 5);
        const detail = err.data.conflicts
          .map(c => `預約 #${c.booking_id}（${c.package_name} ${fmt(c.start_time)}–${fmt(c.end_time)}）`)
          .join("、");
        const confirmed = window.confirm(
          `這位教練同一天已經被指派了其他課程：${detail}\n\n時段有重疊，確定要繼續指派這筆嗎？`
        );
        if (confirmed) handleAssignInternal(bookingId, true);
        return;
      }
      alert(err.message);
    }
  };

  const handleAssignExternal = async (bookingId, force = false) => {
    const form = assigning?.form;
    if (!form?.name || !form?.fee) { alert("姓名與費用為必填"); return; }
    try {
      await adminRequest(`/api/admin/bookings/${bookingId}/assign-external-coach`, {
        method: "POST",
        body: JSON.stringify({
          name: form.name,
          phone: form.phone || null,
          fee: Number(form.fee),
          reason: form.reason || null,
          notes: form.notes || null,
          certification_note: form.certification_note || null,
          save_to_favorites: form.save_to_favorites,
          force_despite_conflict: force,
        }),
      });
      setPending(prev => prev.filter(p => p.id !== bookingId));
      setAssigning(null);
      setMsg(`預約 #${bookingId} 已成功指派外派教練`);
      if (form.save_to_favorites) {
        const updated = await adminRequest("/api/admin/external-coach-favorites");
        setFavorites(updated);
      }
    } catch (err) {
      if (err.data?.conflict) {
        const fmt = (t) => String(t).slice(0, 5);
        const detail = err.data.conflicts
          .map(c => `預約 #${c.booking_id}（${c.package_name} ${fmt(c.start_time)}–${fmt(c.end_time)}）`)
          .join("、");
        const confirmed = window.confirm(
          `「${form.name}」在這天已經被指派了其他課程：${detail}\n\n時段有重疊，確定要繼續指派這筆嗎？`
        );
        if (confirmed) handleAssignExternal(bookingId, true);
        return;
      }
      alert(err.message);
    }
  };

  const levelLabel = { beginner: "初學者", intermediate: "中級", advanced: "進階" };

  return (
    <div>
      {msg && <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "var(--green)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 14 }} onClick={() => setMsg("")}>{msg}</div>}
      <h3 style={{ fontSize: 20, marginBottom: 8 }}>待指派預約</h3>
      <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 20 }}>
        以下預約因無可用教練自動分配，需要手動指派。
      </p>

      {loading ? <div className="spinner" style={{ margin: "40px auto" }} /> : (
        pending.length === 0 ? (
          <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>
            目前沒有待指派的預約 🎉
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {pending.map(p => {
              const isAssigningThis = assigning?.bookingId === p.id;
              return (
                <div key={p.id} className="card" style={{ padding: 20, borderLeft: "4px solid #F59E0B" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                    <div>
                      <span style={{ fontWeight: 500 }}>預約 #{p.id}</span>
                      <span style={{ margin: "0 8px", color: "var(--muted)" }}>·</span>
                      <span style={{ fontSize: 14 }}>{p.student_name}</span>
                    </div>
                    <span className="badge" style={{ background: "#FEF3C7", color: "#92400E" }}>待指派</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, padding: 12, background: "var(--off)", borderRadius: 8, fontSize: 13, marginBottom: 14 }}>
                    <div><div style={{ color: "var(--muted)", marginBottom: 2 }}>日期</div><div style={{ fontWeight: 500 }}>{formatDate(p.booking_date)}</div></div>
                    <div><div style={{ color: "var(--muted)", marginBottom: 2 }}>方案</div><div style={{ fontWeight: 500 }}>{p.package_name}</div></div>
                    <div><div style={{ color: "var(--muted)", marginBottom: 2 }}>程度</div><div style={{ fontWeight: 500 }}>{levelLabel[p.student_level] || p.student_level}</div></div>
                    <div><div style={{ color: "var(--muted)", marginBottom: 2 }}>費用</div><div style={{ fontWeight: 500 }}>NT$ {Number(p.total_price).toLocaleString()}</div></div>
                  </div>

                  {p.requested_coach_name && (
                    <div style={{
                      background: "#FEF2F2", color: "#991B1B", borderRadius: 8,
                      padding: "8px 14px", fontSize: 13, marginBottom: 14,
                    }}>
                      ⚠️ 學生原本指定「{p.requested_coach_name}」教練，但時段與該教練既有安排重疊，需人工確認
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <select className="input-field" style={{ width: 220 }}
                      value={isAssigningThis ? (assigning.mode === "external" ? "external" : assigning.coachId) : ""}
                      onChange={e => handleSelectChange(p.id, e.target.value)}>
                      <option value="">選擇教練</option>
                      {coaches.filter(c => c.is_active).map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                      <option value="external">🧳 外派教練</option>
                    </select>

                    {isAssigningThis && assigning.mode === "internal" && (
                      <button className="btn btn-primary" style={{ padding: "8px 20px", fontSize: 14 }}
                        onClick={() => handleAssignInternal(p.id)}>
                        確認指派
                      </button>
                    )}
                  </div>

                  {isAssigningThis && assigning.mode === "external" && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--stone)" }}>
                      {favorites.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>從常用清單快速選取</label>
                          <select className="input-field" style={{ width: 240 }} defaultValue=""
                            onChange={e => applyFavorite(e.target.value)}>
                            <option value="">— 選擇常用外派教練 —</option>
                            {favorites.map(f => (
                              <option key={f.id} value={f.id}>{f.name}{f.phone ? `（${f.phone}）` : ""}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div>
                          <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>姓名 *</label>
                          <input className="input-field" value={assigning.form.name}
                            onChange={e => updateExternalForm({ name: e.target.value })} />
                        </div>
                        <div>
                          <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>電話</label>
                          <input className="input-field" value={assigning.form.phone}
                            onChange={e => updateExternalForm({ phone: e.target.value })} />
                        </div>
                        <div>
                          <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>費用 (NT$) *</label>
                          <input className="input-field" type="number" value={assigning.form.fee}
                            onChange={e => updateExternalForm({ fee: e.target.value })} />
                        </div>
                        <div>
                          <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>原因</label>
                          <select className="input-field" value={assigning.form.reason}
                            onChange={e => updateExternalForm({ reason: e.target.value })}>
                            <option value="">— 請選擇 —</option>
                            {ASSIGN_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>證照/資格說明</label>
                          <input className="input-field" value={assigning.form.certification_note}
                            onChange={e => updateExternalForm({ certification_note: e.target.value })} />
                        </div>
                        <div style={{ gridColumn: "1 / -1" }}>
                          <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>備注</label>
                          <textarea className="input-field" rows={2} style={{ resize: "vertical" }} value={assigning.form.notes}
                            onChange={e => updateExternalForm({ notes: e.target.value })} />
                        </div>
                      </div>

                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginBottom: 14, cursor: "pointer" }}>
                        <input type="checkbox" checked={assigning.form.save_to_favorites}
                          onChange={e => updateExternalForm({ save_to_favorites: e.target.checked })} />
                        加入常用外派教練清單
                      </label>

                      <div style={{ display: "flex", gap: 10 }}>
                        <button className="btn btn-primary" style={{ padding: "8px 20px", fontSize: 14 }}
                          onClick={() => handleAssignExternal(p.id)}>
                          確認指派外派教練
                        </button>
                        <button className="btn btn-outline" style={{ padding: "8px 20px", fontSize: 14 }}
                          onClick={() => setAssigning(null)}>
                          取消
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// ================================================================
// 排班日曆
// ================================================================
function SchedulePanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState("week"); // "week" | "month"

  const { seasonStart, seasonEnd } = getSeasonBounds();

  // 預設顯示包含今天的那一週（若今天不在雪季內，落在雪季起點所在週）
  function getMondayOf(date) {
    const d = new Date(date);
    const day = d.getDay(); // 0=Sun
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    d.setHours(0, 0, 0, 0);
    return d;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const anchorDate = (today >= seasonStart && today <= seasonEnd) ? today : seasonStart;

  const [weekStart, setWeekStart]   = useState(() => getMondayOf(anchorDate));
  const [monthCursor, setMonthCursor] = useState(() => new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1));

  // ── 週視圖的 7 天 ──
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  // ── 月視圖的當月所有天數（含補位的前後月空白格）──
  function getMonthGrid(cursor) {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // 週一開頭
    const gridStart = new Date(firstDay);
    gridStart.setDate(firstDay.getDate() - startOffset);

    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      days.push(d);
    }
    return days;
  }

  const monthDates = view === "month" ? getMonthGrid(monthCursor) : [];
  const displayDates = view === "week" ? weekDates : monthDates;

  // ── 載入資料範圍 ──
  useEffect(() => {
    setLoading(true);
    const start = toDateString(displayDates[0]);
    const end   = toDateString(displayDates[displayDates.length - 1]);
    adminRequest(`/api/admin/schedule?start_date=${start}&end_date=${end}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [view, weekStart, monthCursor]);

  const levelLabel = { beginner: "初", intermediate: "中", advanced: "進" };
  const weekdayLabel = ["一", "二", "三", "四", "五", "六", "日"];

  // ── 切換週（鎖定在雪季範圍內）──
  const changeWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    const newMonday = getMondayOf(d);
    const weekEnd = new Date(newMonday);
    weekEnd.setDate(newMonday.getDate() + 6);
    // 只要這週與雪季有重疊就允許切換
    if (weekEnd < seasonStart || newMonday > seasonEnd) return;
    setWeekStart(newMonday);
  };

  // ── 切換月（鎖定在雪季範圍內）──
  const changeMonth = (delta) => {
    const d = new Date(monthCursor);
    d.setMonth(d.getMonth() + delta);
    const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    if (monthEnd < seasonStart || d > seasonEnd) return;
    setMonthCursor(d);
  };

  const canGoPrevWeek  = (() => { const d = new Date(weekStart); d.setDate(d.getDate() - 1); return d >= seasonStart; })();
  const canGoNextWeek  = (() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); return d <= seasonEnd; })();
  const canGoPrevMonth = (() => { const d = new Date(monthCursor); d.setDate(0); return d >= seasonStart; })();
  const canGoNextMonth = (() => { const d = new Date(monthCursor); d.setMonth(d.getMonth() + 1); return d <= seasonEnd; })();

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h3 style={{ fontSize: 20 }}>排班日曆</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" style={{
            padding: "6px 16px", fontSize: 13,
            background: view === "week" ? "var(--ink)" : "var(--white)",
            color: view === "week" ? "var(--white)" : "var(--ink)",
            border: `1.5px solid ${view === "week" ? "var(--ink)" : "var(--stone)"}`,
          }} onClick={() => setView("week")}>週視圖</button>
          <button className="btn" style={{
            padding: "6px 16px", fontSize: 13,
            background: view === "month" ? "var(--ink)" : "var(--white)",
            color: view === "month" ? "var(--white)" : "var(--ink)",
            border: `1.5px solid ${view === "month" ? "var(--ink)" : "var(--stone)"}`,
          }} onClick={() => setView("month")}>月視圖</button>
        </div>
      </div>

      <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
        開放範圍：雪季期間（{seasonStart.toLocaleDateString("zh-TW", { year: "numeric", month: "long" })} – {seasonEnd.toLocaleDateString("zh-TW", { year: "numeric", month: "long" })}）
      </p>

      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginBottom: 20 }}>
        {view === "week" ? (
          <>
            <button className="btn btn-outline" style={{ padding: "6px 14px", fontSize: 13 }} disabled={!canGoPrevWeek} onClick={() => changeWeek(-1)}>← 上一週</button>
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {weekDates[0].toLocaleDateString("zh-TW", { month: "long", day: "numeric" })} – {weekDates[6].toLocaleDateString("zh-TW", { month: "long", day: "numeric" })}
            </span>
            <button className="btn btn-outline" style={{ padding: "6px 14px", fontSize: 13 }} disabled={!canGoNextWeek} onClick={() => changeWeek(1)}>下一週 →</button>
          </>
        ) : (
          <>
            <button className="btn btn-outline" style={{ padding: "6px 14px", fontSize: 13 }} disabled={!canGoPrevMonth} onClick={() => changeMonth(-1)}>← 上個月</button>
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {monthCursor.toLocaleDateString("zh-TW", { year: "numeric", month: "long" })}
            </span>
            <button className="btn btn-outline" style={{ padding: "6px 14px", fontSize: 13 }} disabled={!canGoNextMonth} onClick={() => changeMonth(1)}>下個月 →</button>
          </>
        )}
      </div>

      {loading ? <div className="spinner" style={{ margin: "40px auto" }} /> : (
        <div>
          {/* 星期標頭（月視圖才顯示） */}
          {view === "month" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 6 }}>
              {weekdayLabel.map(w => (
                <div key={w} style={{ textAlign: "center", fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>{w}</div>
              ))}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
            {displayDates.map((d, i) => {
              const dateStr = toDateString(d);
              const inSeason = d >= seasonStart && d <= seasonEnd;
              const inCurrentMonth = view === "week" || d.getMonth() === monthCursor.getMonth();
              const dayBookings = (data?.bookings || []).filter(b => toDateString(new Date(b.booking_date)) === dateStr);
              const dayLeaves   = (data?.leaves   || []).filter(l => toDateString(new Date(l.leave_date))   === dateStr);
              const isToday = toDateString(new Date()) === dateStr;

              return (
                <div key={i} className="card" style={{
                  padding: view === "week" ? 12 : 6,
                  minHeight: view === "week" ? 200 : 90,
                  background: isToday ? "var(--off)" : "var(--white)",
                  opacity: inCurrentMonth ? 1 : 0.35,
                }}>
                  <div style={{ fontSize: view === "week" ? 13 : 11, fontWeight: 600, marginBottom: view === "week" ? 10 : 4, textAlign: view === "week" ? "center" : "left", paddingBottom: view === "week" ? 8 : 0, borderBottom: view === "week" ? "1px solid var(--stone)" : "none" }}>
                    {view === "week" ? `${d.getMonth() + 1}/${d.getDate()} (${weekdayLabel[(d.getDay() + 6) % 7]})` : d.getDate()}
                  </div>

                  {!inSeason ? (
                    view === "week" && <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center", marginTop: 20 }}>非雪季</div>
                  ) : (
                    <>
                      {dayBookings.map(b => (
                        <div key={`b${b.id}`} style={{
                          fontSize: view === "week" ? 11 : 9, padding: view === "week" ? "6px 8px" : "2px 4px", borderRadius: 6, marginBottom: 4,
                          background: b.assignment_status === "pending_assignment" ? "#FEF3C7" : b.is_external_coach ? "#EDE9FE" : "#D1FAE5",
                          color: b.assignment_status === "pending_assignment" ? "#92400E" : b.is_external_coach ? "#5B21B6" : "#065F46",
                        }}>
                          {view === "week" ? (
                            <>
                              <div style={{ fontWeight: 600 }}>{b.package_name}</div>
                              <div>{(b.start_time || "").slice(0,5)}–{(b.end_time || "").slice(0,5)}</div>
                              <div>{b.is_external_coach ? `🧳 ${b.coach_name}（外派）` : (b.coach_name || "⚠️ 待指派")} · {levelLabel[b.student_level]}</div>
                            </>
                          ) : (
                            <div style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                              {b.is_external_coach ? `🧳${b.coach_name}` : (b.coach_name || "⚠️待指派")}
                            </div>
                          )}
                        </div>
                      ))}

                      {dayLeaves.map(l => (
                        <div key={`l${l.id}`} style={{
                          fontSize: view === "week" ? 11 : 9, padding: view === "week" ? "6px 8px" : "2px 4px", borderRadius: 6, marginBottom: 4,
                          background: "#F3F4F6", color: "#6B7280",
                          overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                        }}>
                          🚫 {l.coach_name}{view === "week" && (l.package_name ? `（${l.package_name}）` : "（整天）")}
                        </div>
                      ))}

                      {dayBookings.length === 0 && dayLeaves.length === 0 && view === "week" && (
                        <div style={{ fontSize: 12, color: "var(--muted)", textAlign: "center", marginTop: 20 }}>—</div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ================================================================
// 系統管理
// ================================================================
function SystemPanel() {
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const triggerCancel = async () => {
    setLoading(true);
    try {
      const result = await adminRequest("/api/admin/trigger-cancel-expired", { method: "POST" });
      setMsg(result.message);
    } catch (err) { alert(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div>
      {msg && <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "var(--green)", borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 14 }}>{msg}</div>}
      <h3 style={{ fontSize: 20, marginBottom: 20 }}>系統管理</h3>
      <div className="card" style={{ padding: 24 }}>
        <h4 style={{ marginBottom: 8 }}>手動觸發逾時取消</h4>
        <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 16 }}>立即執行逾時未付款的預約取消，不需要等待每小時的自動排程。</p>
        <button className="btn btn-primary" style={{ padding: "10px 24px" }} disabled={loading} onClick={triggerCancel}>
          {loading ? <span className="spinner" style={{ borderTopColor: "var(--white)" }} /> : "立即執行"}
        </button>
      </div>
    </div>
  );
}

// ================================================================
// 主頁面
// ================================================================
export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [tab, setTab] = useState("users");

  useEffect(() => {
    if (!user || user.role !== "admin") { navigate("/"); }
  }, [user]);

  const tabs = [
    { key: "schedule",  label: "排班日曆" },
    { key: "users",     label: "用戶管理" },
    { key: "coaches",   label: "教練管理" },
    { key: "packages",  label: "課程方案" },
    { key: "pending",   label: "待指派" },
    { key: "bookings",  label: "預約管理" },
    { key: "revenue",   label: "收入報表" },
    { key: "locations", label: "地點管理" },
    { key: "system",    label: "系統管理" },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px" }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 36, marginBottom: 8 }}>管理員後台</h1>
        <p style={{ color: "var(--muted)" }}>系統管理與數據總覽</p>
      </div>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />
      {tab === "schedule"  && <SchedulePanel />}
      {tab === "users"     && <UsersPanel />}
      {tab === "coaches"   && <CoachesPanel />}
      {tab === "packages"  && <PackagesPanel />}
      {tab === "pending"   && <PendingPanel />}
      {tab === "bookings"  && <BookingsPanel />}
      {tab === "revenue"   && <RevenuePanel />}
      {tab === "locations" && <LocationsPanel />}
      {tab === "system"    && <SystemPanel />}
    </div>
  );
}